import { useMemo, useCallback, memo } from "react";
import filters from "byzantine/src/filters";
import { format } from "date-fns";
import { useBreakpoints } from "@narmi/design_system";

import Table, { Column } from "../../Table";

import styles from "./SubscriptionsTable.module.scss";
import MerchantIcon from "../MerchantIcon";

type TableTransaction = API.CardSubscription & {
  card: string;
};

const columns: Column<TableTransaction>[] = [
  {
    header: "DATE",
    accessor: "transaction_date",
    className: styles.dateCol,
    render: (date: unknown) => format(new Date(date as string), "MMM d"),
  },
  {
    header: "MERCHANT",
    accessor: "merchant_name",
    render: (merchantName: unknown) => (
      <div className={styles.merchantCell}>
        <div className={styles.logoWrapper}>
          {/* TODO: Replace with real merchant logo */}
          {/* https://linear.app/narmi/issue/CDB-1044/integrate-the-api-subscriptions-and-merchant-info-removing-the-mock */}
          <MerchantIcon name={merchantName as string} size="s" />
        </div>
        <span>{merchantName as TableTransaction["merchant_name"]}</span>
      </div>
    ),
  },
  {
    header: "CARD",
    accessor: "card",
    hideAt: ["xs", "s", "m"],
    className: styles.cardCol,
  },
  {
    header: "AMOUNT",
    accessor: "amount",
    render: (amount: unknown) =>
      filters.currency(filters.dollarsToPennies(amount as Dollars)),
    className: styles.amountCol,
  },
];

interface SubscriptionsTableProps {
  transactions?: API.CardSubscription[];
  cardName: string;
  onRowClick?: (tx: API.CardSubscription) => void;
}

const SubscriptionsTable = ({
  transactions,
  cardName,
  onRowClick,
}: SubscriptionsTableProps) => {
  const { m } = useBreakpoints();

  const handleRowClick = useCallback(
    (transaction: API.CardSubscription) => {
      if (onRowClick) {
        onRowClick(transaction);
      }
    },
    [onRowClick]
  );

  const transactionsWithCardName: TableTransaction[] = useMemo(
    () =>
      transactions?.map((transaction) => ({
        ...transaction,
        card: cardName,
      })) || ([] as TableTransaction[]),
    [cardName, transactions]
  );

  if (!transactions) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Table<TableTransaction>
        columns={columns}
        data={transactionsWithCardName}
        className={styles.transactionsTable}
        isHeaderVisible={m}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default memo(SubscriptionsTable);
